import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, X, Receipt } from 'lucide-react';
import { format } from 'date-fns';
import { createExpense } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const EXPENSE_CATEGORIES = [
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'taxes', label: 'Taxes' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other', label: 'Other' },
];

export const ExpenseDialog = ({ 
  open, 
  onClose, 
  selectedDate, 
  properties, 
  selectedProperty 
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: selectedProperty?.id || '',
    category: 'other',
    amount: '',
    description: '',
    vendor: '',
    expense_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    receipt_photos: [],
    notes: '',
  });
  const [date, setDate] = useState(selectedDate || new Date());
  const [newReceiptUrl, setNewReceiptUrl] = useState('');

  useEffect(() => {
    if (open) {
      if (selectedProperty) {
        setFormData(prev => ({ ...prev, property_id: selectedProperty.id }));
      }
      if (selectedDate) {
        setDate(selectedDate);
        setFormData(prev => ({ ...prev, expense_date: format(selectedDate, 'yyyy-MM-dd') }));
      }
    }
  }, [open, selectedProperty, selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id || !formData.amount || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      await createExpense({
        ...formData,
        amount: parseFloat(formData.amount) || 0,
      });
      toast.success('Expense recorded successfully');
      resetForm();
      onClose();
    } catch (error) {
      toast.error('Failed to create expense');
    } finally {
      setLoading(false);
    }
  };

  const addReceipt = () => {
    if (newReceiptUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        receipt_photos: [...prev.receipt_photos, newReceiptUrl.trim()]
      }));
      setNewReceiptUrl('');
    }
  };

  const removeReceipt = (index) => {
    setFormData(prev => ({
      ...prev,
      receipt_photos: prev.receipt_photos.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      property_id: selectedProperty?.id || '',
      category: 'other',
      amount: '',
      description: '',
      vendor: '',
      expense_date: format(new Date(), 'yyyy-MM-dd'),
      receipt_photos: [],
      notes: '',
    });
    setDate(new Date());
    setNewReceiptUrl('');
  };

  return (
    <Dialog open={open} onOpenChange={() => { resetForm(); onClose(); }}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Record Expense
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Property Selection */}
          <div className="space-y-2">
            <Label>Property *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
            >
              <SelectTrigger data-testid="expense-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger data-testid="expense-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {EXPENSE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Amount and Vendor Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount ($) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                className="bg-slate-50"
                data-testid="expense-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vendor">Vendor</Label>
              <Input
                id="vendor"
                value={formData.vendor}
                onChange={(e) => setFormData(prev => ({ ...prev, vendor: e.target.value }))}
                placeholder="Home Depot, etc."
                className="bg-slate-50"
                data-testid="expense-vendor-input"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What was this expense for?"
              className="bg-slate-50"
              data-testid="expense-description-input"
            />
          </div>

          {/* Expense Date */}
          <div className="space-y-2">
            <Label>Expense Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-slate-50",
                    !date && "text-muted-foreground"
                  )}
                  data-testid="expense-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    setDate(d);
                    setFormData(prev => ({ 
                      ...prev, 
                      expense_date: d ? format(d, 'yyyy-MM-dd') : '' 
                    }));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional details..."
              className="bg-slate-50"
              rows={2}
              data-testid="expense-notes-input"
            />
          </div>

          {/* Receipt Photos */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Receipt Photos (optional)
            </Label>
            <div className="flex gap-2">
              <Input
                value={newReceiptUrl}
                onChange={(e) => setNewReceiptUrl(e.target.value)}
                placeholder="Paste receipt image URL..."
                className="bg-slate-50 flex-1"
                data-testid="expense-receipt-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="icon"
                onClick={addReceipt}
                disabled={!newReceiptUrl.trim()}
                data-testid="expense-add-receipt-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Receipt Preview Grid */}
            {formData.receipt_photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {formData.receipt_photos.map((photo, index) => (
                  <div key={photo || `receipt-${index}`} className="relative group">
                    <img
                      src={photo}
                      alt={`Receipt ${index + 1}`}
                      className="w-full h-20 object-cover rounded-lg border border-slate-200"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100?text=Invalid+URL';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeReceipt(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`expense-remove-receipt-${index}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => { resetForm(); onClose(); }}
              data-testid="expense-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="expense-submit-btn"
            >
              {loading ? 'Saving...' : 'Save Expense'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseDialog;
